import { cloneElement, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { createPortal } from "react-dom";
import { useAnchoredPanel } from "./useAnchoredPanel.js";

/**
 * TT-1..TT-5: dymki pomocy.
 *
 * JEDNA instancja panelu na caly dokument, nie jeden panel na wyzwalacz. To nie jest
 * mikrooptymalizacja: okno laski z TT-3 (sasiedni element w ciagu 500 ms pokazuje sie
 * OD RAZU) jest mierzone GLOBALNIE. Gdyby kazdy wyzwalacz mial wlasny stan, kazdy
 * liczylby wlasne 700 ms i skanowanie paska ikon znow kazaloby czekac przy kazdej.
 *
 * TT-4: etykieta-wyzwalacz nie dostaje ZADNEJ dekoracji - bez podkreslenia, bez ikony
 * "?" - jedynym sygnalem jest kursor help (klasa .field-label--hint).
 */

/** Id panelu. Stale, bo panel jest jeden - wyzwalacze wskazuja na niego aria-describedby. */
const ID_PANELU = "ds-tooltip";

/** TT-3: okno laski. Wartosc 1:1 z gotowca tooltip.html (GRACE). */
const OKNO_LASKI = 500;

/** Zapasowy czas najechania, gdy nie da sie odczytac tokenu (np. w jsdom). */
const DOMYSLNE_OPOZNIENIE = 700;

interface KontekstTooltipa {
  /** Najechanie kursorem: pokaz po --tooltip-delay albo od razu w oknie laski. */
  zaplanuj(kotwica: HTMLElement, tekst: string): void;
  /** Fokus klawiatura: TT-3 mowi wprost - bez opoznienia, fokus jest intencjonalny. */
  pokazTeraz(kotwica: HTMLElement, tekst: string): void;
  ukryj(kotwica: HTMLElement): void;
}

const Kontekst = createContext<KontekstTooltipa | null>(null);

interface Aktywny {
  kotwica: HTMLElement;
  tekst: string;
}

export function TooltipProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [aktywny, setAktywny] = useState<Aktywny | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const kotwicaRef = useRef<HTMLElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Dla ktorej kotwicy biegnie timer - zeby sprzatanie cudzego wyzwalacza go nie kasowalo. */
  const timerKotwicaRef = useRef<HTMLElement | null>(null);
  const ostatnieUkrycieRef = useRef(Number.NEGATIVE_INFINITY);

  kotwicaRef.current = aktywny?.kotwica ?? null;

  const { styl } = useAnchoredPanel({
    otwarty: aktywny !== null,
    kotwicaRef,
    panelRef,
    preferowanaStrona: "nad",
    wyrownanie: "srodek",
    klucz: aktywny?.kotwica,
  });

  const wyczyscTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const ukryj = useCallback(
    (kotwica: HTMLElement) => {
      // Timer kasujemy tylko wtedy, gdy biegnie dla TEJ kotwicy: odmontowanie jednego wyzwalacza
      // (patrz useEffect w Tooltip) nie moze odwolac dymku, ktory kursor wlasnie zaczal wywolywac
      // na sasiednim.
      if (timerKotwicaRef.current === kotwica) wyczyscTimer();
      setAktywny((poprzedni) => {
        if (poprzedni && poprzedni.kotwica !== kotwica) return poprzedni;
        if (poprzedni) ostatnieUkrycieRef.current = Date.now();
        return null;
      });
    },
    [wyczyscTimer],
  );

  const pokazTeraz = useCallback(
    (kotwica: HTMLElement, tekst: string) => {
      wyczyscTimer();
      setAktywny({ kotwica, tekst });
    },
    [wyczyscTimer],
  );

  const zaplanuj = useCallback(
    (kotwica: HTMLElement, tekst: string) => {
      wyczyscTimer();
      if (Date.now() - ostatnieUkrycieRef.current < OKNO_LASKI) {
        setAktywny({ kotwica, tekst });
        return;
      }
      // Token zna tylko przegladarka - czytamy go, zamiast wpisywac 700 drugi raz.
      const zTokenu = Number.parseInt(
        getComputedStyle(document.documentElement).getPropertyValue("--tooltip-delay"),
        10,
      );
      const opoznienie = Number.isFinite(zTokenu) && zTokenu > 0 ? zTokenu : DOMYSLNE_OPOZNIENIE;
      timerKotwicaRef.current = kotwica;
      timerRef.current = setTimeout(() => setAktywny({ kotwica, tekst }), opoznienie);
    },
    [wyczyscTimer],
  );

  useEffect(() => wyczyscTimer, [wyczyscTimer]);

  const wartosc = useMemo<KontekstTooltipa>(
    () => ({ zaplanuj, pokazTeraz, ukryj }),
    [zaplanuj, pokazTeraz, ukryj],
  );

  return (
    <Kontekst.Provider value={wartosc}>
      {children}
      {createPortal(
        <div
          id={ID_PANELU}
          ref={panelRef}
          role="tooltip"
          className={`tooltip${aktywny ? " is-visible" : ""}`}
          style={styl}
        >
          {aktywny?.tekst ?? ""}
        </div>,
        document.body,
      )}
    </Kontekst.Provider>
  );
}

/**
 * TT-5: opakowuje element, ktory bez wyjasnienia jest nieoczywisty (przycisk
 * tylko-ikona, skrotowa etykieta, uciety tekst, kontrolka wylaczona z powodem).
 *
 * Wyzwalaczem jest owijka, a nie samo dziecko: dzieki temu Tooltip dziala z kazdym
 * elementem, takze takim, ktory nie przyjmuje refa. Dziecko dostaje aria-describedby
 * wskazujace na jedyny panel w dokumencie.
 */
export function Tooltip({ tekst, children }: { tekst: string; children: ReactElement }): React.JSX.Element {
  const kontekst = useContext(Kontekst);
  const hostRef = useRef<HTMLSpanElement>(null);

  // Odmontowanie wyzwalacza W TRAKCIE pokazywania dymku (przycisk "Usun plik" znika razem
  // z wierszem listy) nie wywoluje ani onBlur, ani onMouseLeave: React nie wysyla zdarzen
  // do elementu, ktorego juz nie ma. Dymek zostawal wtedy z kotwica odpieta od dokumentu,
  // czyli getBoundingClientRect() == 0 i wisial w lewym gornym rogu, nad logo. Dlatego
  // sprzatamy sami - `ukryj` ignoruje wywolanie, gdy aktywna jest juz INNA kotwica.
  // Element zapamietujemy w efekcie, bo w cleanupie hostRef.current bywa juz null.
  useEffect(() => {
    const host = hostRef.current;
    if (!kontekst || !host) return;
    return () => kontekst.ukryj(host);
  }, [kontekst]);

  // Brak providera nie moze wywrocic widoku - tooltip po prostu nie dziala.
  // TT-1 to dopuszcza: informacja krytyczna nigdy nie siedzi wylacznie w dymku.
  if (!kontekst) return <>{children}</>;

  const opisane = cloneElement(children as ReactElement<Record<string, unknown>>, {
    "aria-describedby": ID_PANELU,
  });

  return (
    <span
      ref={hostRef}
      className="tooltip-host"
      onMouseEnter={() => hostRef.current && kontekst.zaplanuj(hostRef.current, tekst)}
      onMouseLeave={() => hostRef.current && kontekst.ukryj(hostRef.current)}
      onFocus={() => hostRef.current && kontekst.pokazTeraz(hostRef.current, tekst)}
      onBlur={() => hostRef.current && kontekst.ukryj(hostRef.current)}
      onKeyDown={(e) => {
        // A-4: Esc zamyka wszystko, co sie otworzylo - takze dymek.
        if (e.key === "Escape" && hostRef.current) kontekst.ukryj(hostRef.current);
      }}
    >
      {opisane}
    </span>
  );
}
